import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  IconButton,
  Skeleton,
  Tabs,
  Tab,
  Box,
  Popover,
  Typography,
} from '@mui/material';
import { ArrowBack, WarningAmber, HelpOutline } from '@mui/icons-material';

import Page from '@/components/Page';
import Stack from '@/components/Stack';
import { Title, Small } from '@/components/Typography';
import Badge from '@/components/Badge';
import { WorkersV2API, ConsolidatedSkill } from '@/services/workersV2API';
import SkillOverviewTab from './components/SkillOverviewTab';
import WorkExperienceTab from './components/WorkExperienceTab';
import CertificatesTab from './components/CertificatesTab';
import AIAssessmentTab from './components/AIAssessmentTab';
import QuizAssessmentTab from './components/QuizAssessmentTab';
import UniformsTab from './components/UniformsTab';

const SkillDetailPage = () => {
  const { workerId, skillId } = useParams<{
    workerId: string;
    skillId: string;
  }>();
  const navigate = useNavigate();

  const [skill, setSkill] = useState<ConsolidatedSkill | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [helpAnchor, setHelpAnchor] = useState<HTMLElement | null>(null);

  const fetchSkill = async () => {
    if (!workerId || !skillId) return;

    try {
      setLoading(true);
      setError(null);

      const response = await WorkersV2API.getConsolidatedSkill(
        parseInt(workerId),
        parseInt(skillId),
      );

      if (response.success) {
        setSkill(response.data);
      } else {
        setError('Failed to fetch skill details');
      }
    } catch (err) {
      console.error('Error fetching skill details:', err);
      setError('Failed to fetch skill details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSkill();
  }, [workerId, skillId]);

  const handleBack = () => {
    navigate(`/workers_v2/${workerId}`);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'approved':
        return <Badge label="Approved" status="theme" />;
      case 'rejected':
        return <Badge label="Rejected" status="danger" />;
      case 'in_progress':
        return <Badge label="In Progress" status="warning" />;
      default:
        return <Badge label="Unknown" status="neutral" />;
    }
  };

  if (loading) {
    return (
      <Page
        title={<Skeleton width={200} height={40} />}
        media={
          <IconButton onClick={handleBack}>
            <ArrowBack />
          </IconButton>
        }
      >
        <Stack vertical gap={24}>
          <Skeleton variant="rounded" height={400} />
        </Stack>
      </Page>
    );
  }

  if (error || !skill) {
    return (
      <Page
        title="Skill Not Found"
        media={
          <IconButton onClick={handleBack}>
            <ArrowBack />
          </IconButton>
        }
      >
        <Stack vertical gap={24}>
          <Small color="lighter">{error || 'Skill not found'}</Small>
        </Stack>
      </Page>
    );
  }

  const parsedWorkerId = parseInt(workerId!);

  const workerSkill = skill.worker_skill;
  const skillRequest = skill.skill_request;

  const tabWarnings = {
    overview:
      !!workerSkill?.disabled ||
      !!workerSkill?.expired ||
      (workerSkill?.days_until_expiration != null &&
        workerSkill.days_until_expiration <= 30),
    certificates: skillRequest?.certificates?.some(
      (c) => c.status === 'pending',
    ),
    uniforms: skillRequest?.uniforms?.some((u) => u.status === 'pending'),
    aiAssessment: skill.ai_assessment?.final_status === 'for_review',
    quiz:
      (skillRequest &&
        !skillRequest.assessment_passed &&
        !!skill.assessment_answer) ||
      !!skillRequest?.assessment_permanently_ineligible,
  };

  const tabLabel = (label: string, hasWarning?: boolean) =>
    hasWarning ? (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {label}
        <WarningAmber sx={{ fontSize: 16, color: '#F59E0B' }} />
      </Box>
    ) : (
      label
    );

  return (
    <Page
      size="xl"
      title={
        <Stack vertical gap={8}>
          <Stack gap={12} style={{ alignItems: 'center' }}>
            <Title>{skill.skill_name}</Title>
            {getStatusBadge(skill.status)}
            <IconButton
              size="small"
              onClick={(e) => setHelpAnchor(e.currentTarget)}
            >
              <HelpOutline fontSize="small" />
            </IconButton>
          </Stack>
          {skill.skill_name_es && (
            <Small color="lighter">{skill.skill_name_es}</Small>
          )}
        </Stack>
      }
      media={
        <IconButton onClick={handleBack}>
          <ArrowBack />
        </IconButton>
      }
    >
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label={tabLabel('Overview', tabWarnings.overview)} />
          <Tab label="Work Experience" />
          <Tab label={tabLabel('Certificates', tabWarnings.certificates)} />
          <Tab label={tabLabel('Uniforms', tabWarnings.uniforms)} />
          <Tab label={tabLabel('AI Assessment', tabWarnings.aiAssessment)} />
          <Tab label={tabLabel('Quiz', tabWarnings.quiz)} />
        </Tabs>
      </Box>

      {activeTab === 0 && (
        <SkillOverviewTab
          skill={skill}
          workerId={parsedWorkerId}
          onUpdate={fetchSkill}
        />
      )}
      {activeTab === 1 && (
        <WorkExperienceTab
          skill={skill}
          workerId={parsedWorkerId}
          onUpdate={fetchSkill}
        />
      )}
      {activeTab === 2 && (
        <CertificatesTab
          skill={skill}
          workerId={parsedWorkerId}
          onUpdate={fetchSkill}
        />
      )}
      {activeTab === 3 && (
        <UniformsTab
          skill={skill}
          workerId={parsedWorkerId}
          onUpdate={fetchSkill}
        />
      )}
      {activeTab === 4 && (
        <AIAssessmentTab
          skill={skill}
          workerId={parsedWorkerId}
          onUpdate={fetchSkill}
        />
      )}
      {activeTab === 5 && (
        <QuizAssessmentTab
          skill={skill}
          workerId={parsedWorkerId}
          onUpdate={fetchSkill}
        />
      )}
      <Popover
        open={!!helpAnchor}
        anchorEl={helpAnchor}
        onClose={() => setHelpAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <div style={{ padding: '16px', maxWidth: '360px' }}>
          <Typography variant="subtitle2" gutterBottom>
            How Skill Records Work
          </Typography>
          <Typography variant="body2" paragraph style={{ marginBottom: 8 }}>
            The <strong>Worker Skill</strong> is the master record that
            determines whether a worker can claim jobs requiring this skill. If
            no Worker Skill exists or it is disabled, the worker cannot claim
            jobs for this skill.
          </Typography>
          <Typography variant="body2" paragraph style={{ marginBottom: 8 }}>
            The <strong>Skill Request</strong> tracks the worker's application
            for this skill, including certificates, uniforms, work experience,
            and assessments.
          </Typography>
          <Typography variant="body2" style={{ marginBottom: 0 }}>
            Approving a skill request creates the Worker Skill record. Rejecting
            it does not remove an existing Worker Skill.
          </Typography>
        </div>
      </Popover>
    </Page>
  );
};

export default SkillDetailPage;
